import { useEffect, useRef, useState } from 'react';
import { supabase, type NewsItem } from '@/lib/supabase';
import NewsForm from './components/NewsForm';
import GenerateNewsModal from './components/GenerateNewsModal';
import CronTab from './components/CronTab';
import SitemapTab from './components/SitemapTab';
import CommentNotificationPanel from './components/CommentNotificationPanel';
import StatsTab from './components/StatsTab';
import NewsPreviewModal from './components/NewsPreviewModal';

const ADMIN_PASSWORD = 'Meay4bbh545406061';
const SESSION_KEY = 'admin_auth';

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onLogin();
    } else {
      setError('Şifre yanlış, tekrar deneyin.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPw('');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4">
      <div className={`bg-white rounded-2xl p-8 w-full max-w-sm ${shake ? 'animate-bounce' : ''}`}>
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center bg-[#0A0E27] rounded-2xl mx-auto mb-4">
            <i className="ri-shield-keyhole-line text-[#00D9FF] text-2xl" />
          </div>
          <h1 className="text-xl font-black text-[#0A0E27]">Admin Girişi</h1>
          <p className="text-gray-400 text-sm mt-1">TeknoNovaHub Yönetim Paneli</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Şifre</label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={pw}
                onChange={e => { setPw(e.target.value); setError(''); }}
                placeholder="Admin şifresini girin"
                autoFocus
                className="w-full border border-gray-200 rounded-lg px-4 py-3 pr-10 text-sm focus:outline-none focus:border-[#0A0E27]"
              />
              <button
                type="button"
                onClick={() => setShow(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-6 h-6 flex items-center justify-center"
              >
                <i className={show ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2.5">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-[#0A0E27] text-white rounded-lg py-3 text-sm font-bold hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap"
          >
            Giriş Yap
          </button>
        </form>

        <p className="text-center text-xs text-gray-300 mt-6">
          <a href="/" className="hover:text-gray-500 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-1" />Siteye Dön
          </a>
        </p>
      </div>
    </div>
  );
}

type Tab = 'news' | 'comments' | 'stats' | 'cron' | 'sitemap';
type CommentItem = {
  id: string; news_id: string; name: string; email: string;
  comment: string; approved: boolean; created_at: string;
  news?: { title: string };
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const [tab, setTab] = useState<Tab>('news');
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<NewsItem | null>(null);
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [previewItem, setPreviewItem] = useState<NewsItem | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState<'all' | 'featured' | 'normal'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'views' | 'title'>('date');
  const [newCommentNotifs, setNewCommentNotifs] = useState<CommentItem[]>([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleGenerated = (count: number, category: string) => {
    fetchNews();
    showToast(`${count} yeni haber "${category}" kategorisine eklendi!`);
  };

  const fetchNews = async () => {
    setLoading(true);
    const { data } = await supabase.from('news').select('*').order('created_at', { ascending: false });
    setNewsList(data || []);
    setLoading(false);
  };

  const fetchComments = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('news_comments')
      .select('*, news(title)')
      .order('created_at', { ascending: false });
    setComments((data as CommentItem[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchNews(); fetchComments(); }, []);

  // Realtime yorum bildirimi
  useEffect(() => {
    const channel = supabase
      .channel('admin-comment-notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'news_comments' },
        async (payload) => {
          const newComment = payload.new as CommentItem;
          // Haberin başlığını çek
          const { data: newsData } = await supabase
            .from('news')
            .select('title')
            .eq('id', newComment.news_id)
            .maybeSingle();
          const enriched = { ...newComment, news: newsData ? { title: newsData.title } : undefined };
          setNewCommentNotifs(prev => [enriched, ...prev].slice(0, 10));
          setComments(prev => [enriched, ...prev]);
          showToast(`Yeni yorum: ${newComment.name}`);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Bildirim paneli dışına tıklayınca kapat
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleDelete = async (id: string) => {
    await supabase.from('news').delete().eq('id', id);
    setNewsList(p => p.filter(n => n.id !== id));
    setDeleteConfirm(null);
    showToast('Haber silindi.');
  };

  const handleToggleFeatured = async (id: string, currentFeatured: boolean) => {
    await supabase.from('news').update({ featured: !currentFeatured }).eq('id', id);
    setNewsList(p => p.map(n => n.id === id ? { ...n, featured: !currentFeatured } : n));
    showToast(!currentFeatured ? 'Haber öne çıkarıldı!' : 'Haber öne çıkarma kaldırıldı.');
  };

  const handleApproveComment = async (id: string) => {
    await supabase.from('news_comments').update({ approved: true }).eq('id', id);
    setComments(p => p.map(c => c.id === id ? { ...c, approved: true } : c));
    showToast('Yorum onaylandı!');
  };

  const handleDeleteComment = async (id: string) => {
    await supabase.from('news_comments').delete().eq('id', id);
    setComments(p => p.filter(c => c.id !== id));
    showToast('Yorum silindi.');
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedItems(p => [...p, id]);
    } else {
      setSelectedItems(p => p.filter(item => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(filteredNews.map(n => n.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleBulkDelete = async () => {
    for (const id of selectedItems) {
      await supabase.from('news').delete().eq('id', id);
    }
    setNewsList(p => p.filter(n => !selectedItems.includes(n.id)));
    setSelectedItems([]);
    setShowBulkDelete(false);
    showToast(`${selectedItems.length} haber silindi.`);
  };

  const handleBulkFeatured = async (makeFeatured: boolean) => {
    await supabase.from('news').update({ featured: makeFeatured }).in('id', selectedItems);
    setNewsList(p => p.map(n => selectedItems.includes(n.id) ? { ...n, featured: makeFeatured } : n));
    setSelectedItems([]);
    showToast(makeFeatured
      ? `${selectedItems.length} haber öne çıkarıldı!`
      : `${selectedItems.length} haberin öne çıkarması kaldırıldı.`
    );
  };

  const filteredNews = newsList
    .filter(n =>
      (n.title.toLowerCase().includes(search.toLowerCase()) ||
       n.author.toLowerCase().includes(search.toLowerCase())) &&
      (categoryFilter ? n.category === categoryFilter : true) &&
      (featuredFilter === 'featured' ? n.featured : featuredFilter === 'normal' ? !n.featured : true)
    )
    .sort((a, b) => {
      if (sortBy === 'views') return (b.views || 0) - (a.views || 0);
      if (sortBy === 'title') return a.title.localeCompare(b.title, 'tr');
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

  const uniqueCategories = Array.from(new Set(newsList.map(n => n.category))).sort();

  const pendingComments = comments.filter(c => !c.approved).length;

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      {/* Header */}
      <div className="bg-[#0A0E27] text-white px-6 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#00D9FF]/20 rounded-lg">
            <i className="ri-shield-check-line text-[#00D9FF]" />
          </div>
          <div>
            <h1 className="font-bold text-base">Admin Paneli</h1>
            <p className="text-xs text-white/40">TeknoNovaHub Yönetim</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGenerate(true)}
            className="flex items-center gap-1.5 bg-[#00FF88] text-[#0A0E27] text-xs font-bold px-4 py-2 rounded-full hover:bg-[#00FF88]/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-magic-line" />Haber Üret
          </button>
          <button
            onClick={() => { setEditItem(null); setShowForm(true); }}
            className="flex items-center gap-1.5 bg-[#00D9FF] text-[#0A0E27] text-xs font-bold px-4 py-2 rounded-full hover:bg-[#00D9FF]/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />Yeni Haber
          </button>
          {/* Bildirim Çanı */}
          <div ref={notifRef} className="relative">
            <button
              onClick={() => { setShowNotifPanel(v => !v); setNewCommentNotifs([]); }}
              className="relative w-9 h-9 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-notification-3-line text-white text-base" />
              {newCommentNotifs.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-black rounded-full animate-pulse">
                  {newCommentNotifs.length > 9 ? '9+' : newCommentNotifs.length}
                </span>
              )}
            </button>
            {showNotifPanel && (
              <CommentNotificationPanel
                notifications={newCommentNotifs.length > 0 ? newCommentNotifs : comments.filter(c => !c.approved).slice(0, 5)}
                isEmpty={newCommentNotifs.length === 0 && comments.filter(c => !c.approved).length === 0}
                onGoToComments={() => { setTab('comments'); setShowNotifPanel(false); }}
                onApprove={async (id) => { await handleApproveComment(id); setNewCommentNotifs(p => p.filter(n => n.id !== id)); }}
              />
            )}
          </div>
          <a href="/" className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line" />Siteye Dön
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-xs text-white/60 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line" />Çıkış
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 pb-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Toplam Haber', value: newsList.length, icon: 'ri-newspaper-line', color: 'text-[#0A0E27]' },
            { label: 'Öne Çıkan', value: newsList.filter(n => n.featured).length, icon: 'ri-star-line', color: 'text-yellow-500' },
            { label: 'Bekleyen Yorum', value: pendingComments, icon: 'ri-chat-3-line', color: 'text-orange-500' },
            { label: 'Toplam Yorum', value: comments.length, icon: 'ri-message-2-line', color: 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 ${s.color}`}>
                <i className={`${s.icon} text-lg`} />
              </div>
              <div>
                <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-400">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1 mb-5 w-fit">
          {([
            { key: 'news', label: 'Haberler', icon: 'ri-newspaper-line' },
            { key: 'comments', label: `Yorumlar${pendingComments > 0 ? ` (${pendingComments})` : ''}`, icon: 'ri-chat-3-line' },
            { key: 'stats', label: 'İstatistikler', icon: 'ri-bar-chart-2-line' },
            { key: 'cron', label: 'Otomatik Haber', icon: 'ri-timer-flash-line' },
            { key: 'sitemap', label: 'SEO & Sitemap', icon: 'ri-map-2-line' },
          ] as { key: Tab; label: string; icon: string }[]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${tab === t.key ? 'bg-[#0A0E27] text-white' : 'text-gray-500 hover:text-[#0A0E27]'}`}
            >
              <i className={t.icon} />{t.label}
            </button>
          ))}
        </div>

        {/* News Tab */}
        {tab === 'news' && (
          <>
            {/* Search and Filters */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Haber veya yazar ara..."
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0A0E27]"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-600 focus:outline-none focus:border-[#0A0E27] cursor-pointer"
                >
                  <option value="">Tüm Kategoriler</option>
                  {uniqueCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                {/* Featured Filter */}
                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
                  {([['all', 'Tümü'], ['featured', 'Öne Çıkan'], ['normal', 'Normal']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setFeaturedFilter(val)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${featuredFilter === val ? 'bg-[#0A0E27] text-white' : 'text-gray-500 hover:text-[#0A0E27]'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as 'date' | 'views' | 'title')}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-600 focus:outline-none focus:border-[#0A0E27] cursor-pointer"
                >
                  <option value="date">Tarihe Göre</option>
                  <option value="views">Görüntülemeye Göre</option>
                  <option value="title">Başlığa Göre</option>
                </select>
              </div>

              {/* Result count + bulk actions */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {filteredNews.length} / {newsList.length} haber gösteriliyor
                </span>
                {selectedItems.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg">
                      <i className="ri-checkbox-multiple-line mr-1" />{selectedItems.length} seçili
                    </span>
                    <button
                      onClick={() => handleBulkFeatured(true)}
                      className="flex items-center gap-1.5 bg-yellow-400 text-yellow-900 text-xs font-bold px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-star-fill" />Öne Çıkar
                    </button>
                    <button
                      onClick={() => handleBulkFeatured(false)}
                      className="flex items-center gap-1.5 bg-gray-200 text-gray-600 text-xs font-bold px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-star-line" />Öne Çıkarmayı Kaldır
                    </button>
                    <div className="w-px h-5 bg-gray-200" />
                    <button
                      onClick={() => setShowBulkDelete(true)}
                      className="flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line" />Toplu Sil
                    </button>
                    <button
                      onClick={() => setSelectedItems([])}
                      className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
                    >
                      İptal
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bulk Delete Confirmation */}
            {showBulkDelete && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700 font-semibold mb-1">
                      {selectedItems.length} haber kalıcı olarak silinecek!
                    </p>
                    <p className="text-xs text-red-600 mb-3">
                      Bu işlem geri alınamaz. Emin misiniz?
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleBulkDelete}
                        className="bg-red-500 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
                      >
                        Evet, Sil
                      </button>
                      <button
                        onClick={() => setShowBulkDelete(false)}
                        className="bg-white text-gray-600 text-xs font-bold px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        İptal
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* News List */}
            {loading ? (
              <div className="text-center py-16 text-gray-400">
                <i className="ri-loader-4-line text-3xl animate-spin block mb-3" />Yükleniyor...
              </div>
            ) : filteredNews.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100 text-gray-400">
                <i className="ri-newspaper-line text-4xl block mb-3" />
                <p className="text-sm">Haber bulunamadı.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {/* Table Header */}
                <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredNews.length && filteredNews.length > 0}
                    onChange={e => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0A0E27] focus:ring-[#0A0E27] cursor-pointer"
                  />
                  <span className="text-xs font-semibold text-gray-500">Tümünü Seç</span>
                </div>
                
                {/* News Items */}
                <div className="flex flex-col">
                  {filteredNews.map(item => (
                    <div key={item.id} className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={e => handleSelectItem(item.id, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#0A0E27] focus:ring-[#0A0E27] cursor-pointer flex-shrink-0"
                      />
                      <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-sm font-semibold text-[#0A0E27] line-clamp-1">{item.title}</h3>
                          {item.featured && (
                            <span className="flex-shrink-0 text-[10px] bg-yellow-100 text-yellow-700 font-bold px-2 py-0.5 rounded-full">Öne Çıkan</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span>{item.author}</span><span>·</span><span>{item.date}</span><span>·</span>
                          <span className="capitalize">{item.category}</span><span>·</span>
                          <span>{item.views?.toLocaleString()} görüntüleme</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Featured Toggle */}
                        <button
                          onClick={() => handleToggleFeatured(item.id, !!item.featured)}
                          title={item.featured ? 'Öne çıkarmayı kaldır' : 'Öne çıkar'}
                          className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                            item.featured
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-yellow-50 hover:text-yellow-600'
                          }`}
                        >
                          <i className={item.featured ? 'ri-star-fill' : 'ri-star-line'} />
                          {item.featured ? 'Öne Çıkan' : 'Öne Çıkar'}
                        </button>
                        <button
                          onClick={() => setPreviewItem(item)}
                          className="flex items-center gap-1 bg-[#00D9FF]/10 text-[#00D9FF] rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#00D9FF]/20 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-eye-line" />Önizle
                        </button>
                        <a
                          href={`/haber/${item.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 bg-gray-100 text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-external-link-line" />Gör
                        </a>
                        <button
                          onClick={() => { setEditItem(item); setShowForm(true); }}
                          className="flex items-center gap-1 bg-gray-100 text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line" />Düzenle
                        </button>
                        {deleteConfirm === item.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDelete(item.id)} className="bg-red-500 text-white rounded-lg px-3 py-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap">Evet</button>
                            <button onClick={() => setDeleteConfirm(null)} className="bg-gray-100 text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap">İptal</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(item.id)}
                            className="flex items-center gap-1 bg-red-50 text-red-500 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Stats Tab */}
        {tab === 'stats' && <StatsTab />}

        {/* Cron Tab */}
        {tab === 'cron' && <CronTab />}

        {/* Sitemap Tab */}
        {tab === 'sitemap' && <SitemapTab />}

        {/* Comments Tab */}
        {tab === 'comments' && (
          <div className="flex flex-col gap-3">
            {loading ? (
              <div className="text-center py-16 text-gray-400">
                <i className="ri-loader-4-line text-3xl animate-spin block mb-3" />Yükleniyor...
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100 text-gray-400">
                <i className="ri-chat-off-line text-4xl block mb-3" />
                <p className="text-sm">Henüz yorum yok.</p>
              </div>
            ) : (
              comments.map(c => (
                <div key={c.id} className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-semibold text-[#0A0E27] text-sm">{c.name}</span>
                        <span className="text-xs text-gray-400">{c.email}</span>
                        {c.approved
                          ? <span className="text-[10px] bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">Onaylı</span>
                          : <span className="text-[10px] bg-orange-100 text-orange-600 font-bold px-2 py-0.5 rounded-full">Bekliyor</span>
                        }
                      </div>
                      {c.news && <p className="text-xs text-gray-400 mb-2 line-clamp-1"><i className="ri-newspaper-line mr-1" />{c.news.title}</p>}
                      <p className="text-sm text-gray-600 leading-relaxed">{c.comment}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!c.approved && (
                        <button onClick={() => handleApproveComment(c.id)} className="flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-check-line" />Onayla
                        </button>
                      )}
                      <button onClick={() => handleDeleteComment(c.id)} className="flex items-center gap-1 bg-red-50 text-red-500 border border-red-200 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-delete-bin-line" />Sil
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0A0E27] text-white text-sm px-5 py-3 rounded-full z-50 flex items-center gap-2">
          <i className="ri-check-line text-[#00FF88]" />{toast}
        </div>
      )}

      {/* Generate Modal */}
      {showGenerate && (
        <GenerateNewsModal
          onClose={() => setShowGenerate(false)}
          onGenerated={(count, category) => {
            setShowGenerate(false);
            handleGenerated(count, category);
          }}
        />
      )}

      {/* News Form Modal */}
      {showForm && (
        <NewsForm
          editItem={editItem}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={() => { setShowForm(false); setEditItem(null); fetchNews(); showToast(editItem ? 'Haber güncellendi!' : 'Haber yayınlandı!'); }}
        />
      )}

      {/* News Preview Modal */}
      {previewItem && (
        <NewsPreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onEdit={() => {
            setEditItem(previewItem);
            setPreviewItem(null);
            setShowForm(true);
          }}
        />
      )}
    </div>
  );
}
